import * as d3 from  "d3";
import _ from 'lodash';
import * as planck from 'planck-js'; // "planck-js": "^0.3.31", [abandon] "^0.1.34"
import { MainViewTypes } from '../../../../types/data';

// 2-1. compact layout, using force-directed
export const getCircleCentroid = (circles: any) => {
    // console.log('=========== getCircleCentroid() ==============')
    // console.log('circles: ', circles.map((circle) => circle.data.name))

    // Initialize variables for calculating the centroid:
    // The function starts by setting up a variable to sum the mass of all circles (circleMassSum) and another to represent the centroid as a vector (centroid) initialized at zero.
    // Calculate centroid of circle group.
    let circleMassSum = 0;
    let centroid = planck.Vec2.zero();

    // Iterate over the circles to compute the centroid:
    circles.forEach(function(circle: any) {
        // For each circle, its mass is calculated as the area of the circle (πr^2) because mass is proportional to the area for uniform density circles.
        let circleMass = circle.r * circle.r * Math.PI;

        // This mass is added to the total mass sum, and the circle's contribution to the centroid's coordinates is accumulated by multiplying the circle's coordinates by its mass (effectively a weighted sum).
        circleMassSum += circleMass;
        centroid.x += circle.x * circleMass;
        centroid.y += circle.y * circleMass;
    });

    // Normalize the centroid coordinates: After summing, the centroid's coordinates are divided by the total mass to get the average position, yielding the centroid of the circles.
    centroid.mul(1.0/circleMassSum);

    // Return the centroid: The function returns the calculated centroid vector.
    return centroid;
}
export const getLayerClusters = (hierarchyRoot: any, layerDepth: number, padding: number) => {
    // console.log('\n\n====== getLayerClusters() ======')
    // console.log('hierarchyRoot: ', hierarchyRoot)
    // console.log('\n\n')
    var clusters: any = []

    // selects nodes from the hierarchy that are exactly at the given layerDepth
    let layerNodes = hierarchyRoot.descendants().filter(function(candidate: any) {
        return candidate.depth === layerDepth;
    });
    // console.log('layerNodes: ', layerNodes)

    // iterate over these nodes to form clusters:
    layerNodes.forEach(function(node: any) {
        // For each node found,

        // it gets the descendants of that node which do not have any children (i.e., leaf nodes) to form a cluster of nodes.
        let clusterChildren = node.descendants().filter(function(candidate: any){
            return !candidate.children;
        });

        // It identifies the cluster's parent node, which is the ancestor of the current node that is also at the specified layerDepth.
        let clusterParent = node.ancestors().filter(function(ancestor: any) {
            return ancestor.depth === layerDepth;
        })[0];

        // debug
        // const parentList = node.ancestors().filter(function(ancestor: any) {
        // return ancestor.depth === layerDepth;
        // })
        // console.log('parentList: ', parentList)
        // console.log('node: ', node.data.name)
        // console.log('node.ancestors(): ', node.ancestors())
        // console.log('clusterParent: ', clusterParent)
        // console.log('\n')

        // For each node in the cluster, it calculates padding for its contour and a kind of layout spacing (referred to as planckPadding in the code), based on the depth difference between the node and its cluster parent, and applies an additional padding for spacing between clusters if there are multiple nodes in the cluster.
        clusterChildren.forEach(function(node: any) {
            // let path = node.path(clusterParent).slice(1,-1);
            // let uncertaintySum = path.reduce(function(acc: any, pathnode: any){
            //     return acc + pathnode.uncertainty;
            // }, 0);

            // let contourClusterParentUncertainty = clusterParent.uncertainty/2;                                // Padding for contour (contour lies 50% outside of parent node).
            // let planckClusterParentUncertainty = node !== clusterParent ? clusterParent.uncertainty : 0;    // Padding for force based layout (contours should not cut each other, i.e. full parent contour should be taken into account).

            let interClusterSpacing = clusterChildren.length === 1 ? 0 : padding / 2.0; // For single circle: No padding, since it has no contour.
            node.contourPadding = (node.depth - clusterParent.depth) * padding;
            node.planckPadding = (node.depth - clusterParent.depth) * padding + interClusterSpacing;

            // node.contourPadding = (node.depth - clusterParent.depth) * padding + uncertaintySum + contourClusterParentUncertainty;
            // node.planckPadding = (node.depth - clusterParent.depth) * padding + uncertaintySum + planckClusterParentUncertainty + interClusterSpacing;
        });

        clusters.push({
            nodes: clusterChildren, // node: actually is the children of the cluster
            parent: clusterParent
        });
    });

    return clusters;
}
export const createClusterBody = (layerCluster: any, world: any) => {
    // console.log('=========== createClusterBody() ==============')

    // Calculate the centroid of the cluster: It uses the getCircleCentroid() function to find the geometric center (centroid) of all the circles in the layerCluster. This centroid acts as the position around which the cluster's circles are arranged.
    let bodyCentroid = getCircleCentroid(layerCluster.nodes);
    // console.log('bodyCentroid: ', bodyCentroid)

    // Create a dynamic body in the physics world: A dynamic body, which can move and interact with other bodies under physics simulation, is created at the position of the calculated centroid (bodyCentroid). This body represents the entire cluster in the physics simulation.
    let body = world.createDynamicBody(bodyCentroid);

    // Add circles as fixtures -- 每个circle的属性
    // Define fixture properties: The circleFD object defines "the properties for each circle" (fixture) that will be added to the dynamic body. 定义夹具属性：circleFD 对象定义将添加到动态主体的“每个圆的属性”（夹具）。
        // The density is set to 1.0, making the mass of the fixture proportional to its area (since mass = density × area for circles in a 2D physics engine). 密度设置为 1.0，使夹具的质量与其面积成正比（因为在 2D 物理引擎中，质量 = 密度 × 圆的面积）
        // The friction is set very low (0.00001) to minimize energy loss during interactions, allowing the circles to slide past each other easily. 摩擦力设置得非常低（0.00001），以最大限度地减少交互过程中的能量损失，从而使圆圈可以轻松地相互滑过。
    let circleFD = {
        density: 1.0,
        friction: 0.00001
    };

    // Add each circle as a fixture to the body: For each circle in the cluster:
    layerCluster.nodes.forEach(function(circle: any) {
        // The circle's [global position] is converted to a planck.Vec2 object.
        let centerGlobal = planck.Vec2(circle.x, circle.y);

        // The circle's [local position] relative to the body's centroid is calculated by subtracting the centroid's position from the circle's global position.
        // This step is crucial because, in the physics simulation, the body's position is its centroid, and fixtures (circles) must be positioned relative to this point.
        let centerLocal = centerGlobal.sub(bodyCentroid);

        // A circular fixture is created with (1) the local center and (2) the circle's radius, which is adjusted by adding circle.planckPadding. 使用 (1) 局部中心和 (2) 圆的半径创建圆形夹具，通过添加circle.planckPadding 来调整圆的半径。
        // This padding likely accounts for additional spacing to prevent overlaps or to ensure there's a buffer between circles in the simulation. The fixture is added to the dynamic body with the previously defined density and friction properties. 这种填充可能会产生额外的间距，以防止重叠或确保模拟中的圆圈之间有缓冲区。将夹具添加到具有先前定义的密度和摩擦属性的动态主体中。
        // circle.finalScale = circle.r + circle.planckPadding;
        let fixture = body.createFixture(planck.Circle(centerLocal, circle.r + circle.planckPadding), circleFD);

        // Each fixture is associated with its corresponding circle data (setUserData(circle)), allowing for easy reference back to the original data object when needed, such as after the simulation to update positions.
        fixture.setUserData(circle);
    });

    // Return completed body.
    // Return the dynamic body: After all circles are added as fixtures, the dynamic body, now representing the cluster, is returned. This body is then integrated into the physics world's simulation, participating in interactions according to the physics engine's rules.
    return body;
}
export const layoutClusters = (hierarchyRoot: any, layerClusters: any, centroid: any) => {
    // console.log('--------- layoutClusters() ---------')
    // console.log('layerClusters: ', layerClusters)
    // console.log('layerClusters: ', layerClusters.map((cluster: any)=> cluster.nodes.map((node: any) => node.data.name)))
    // Create a physics world: A new physics world is created with zero gravity (gravity: planck.Vec2(0,0)), suitable for simulating forces among objects in a 2D space without gravitational pull. (适合模拟 没有引力的二维空间中 物体之间的力)
    let world = planck.World({
        gravity: planck.Vec2(0,0)
    });

    // Create bodies for groups.
    // Create physics bodies for clusters: Iterates through each layer cluster to create a physical body representation in the physics world for each cluster. The createClusterBody function (not provided) likely sets up these bodies with properties (such as position and possibly mass) derived from the clusters.
    let layerClusterBodies: any[] = [];
    layerClusters.forEach(function(layerCluster: any) {
        layerClusterBodies.push(createClusterBody(layerCluster, world));
    });

    // Create attractor. (在质心位置创建静态体。该物体充当cluster的吸引点)
    // Create an attractor body: A static body is created at the centroid position. This body acts as a point of attraction for the cluster bodies.
    let attractorBody = world.createBody(planck.Vec2(centroid.x, centroid.y));

    // Create joints between layerClusterBodies and attractor.
    // Create joints to simulate attraction: For each cluster body, a distance joint is created between the body and the attractor body. The joint's properties (like frequencyHz and dampingRatio) are configured to control the elasticity and damping of the simulated attraction, with the aim of minimizing overlaps among the clusters. 创建关节来模拟吸引力：对于每个簇主体，在主体和吸引子主体之间创建一个距离关节。关节的属性（如频率赫兹和阻尼比）配置为控制模拟吸引力的弹性和阻尼，目的是最大限度地减少集群之间的重叠。
    layerClusterBodies.forEach((layerClusterBody) => {
        let distanceJoint = planck.DistanceJoint( {// https://github.com/piqnt/planck.js/blob/master/docs/api/classes/distancejoint.md
                frequencyHz : 0.9, // TODO: Try to avoid overlapping in large datasets!
                dampingRatio : 0.001 // TODO: ''
            },
            attractorBody,
            layerClusterBody,
            attractorBody.getPosition(),
            layerClusterBody.getPosition()
        );
        // console.log('distanceJoint.m_length = ' + distanceJoint.m_length, typeof distanceJoint.m_length)
        // @ts-ignore
        distanceJoint.m_length = 0; // Set the length to zero as it's calculated as the distance between the anchors. TODO: PR on planck-js repo to fix bug.
        // console.log('--> after, distanceJoint.m_length = ' + distanceJoint.m_length, typeof distanceJoint.m_length)
        world.createJoint(distanceJoint);
    })

    // Prepare for simulation. Typically we use a time step of 1/60 of a
    // second (60Hz) and 10 iterations. This provides a high quality simulation
    // in most game scenarios.
    let timestep = 1.0 / 60.0;
    let velocityIterations = 6;
    let positionIterations = 2;

    // Simulation loop.
    // Run the simulation: The world is stepped through a number of iterations (1000 in this case) to allow the physics simulation to stabilize. The time step and iteration counts are chosen based on common physics simulation practices for games and simulations.
    for (let i = 0; i < 1000; ++i) {
        // Instruct the world to perform a single step of simulation.
        // It is generally best to keep the time step and iterations fixed.
        world.step(timestep, velocityIterations, positionIterations);
    }
    // console.log('world: ', world)

    // Write results back to hierarchy.
    // Apply simulation results: After the simulation, the new positions of the bodies (representing clusters) are read back from the physics world. Each body's position is updated based on the simulation results, effectively rearranging the clusters around the centroid in a manner that reduces overlap and presumably achieves a visually appealing layout. 模拟后，从物理世界读回物体（代表簇）的新位置。每个物体的位置都会根据模拟结果进行更新，以减少重叠的方式有效地重新排列质心周围的簇，并可能实现视觉上吸引人的布局。
    const leafNodes_UpdatedXY: any = []
    for (let body = world.getBodyList(); body; body = body.getNext()) {
        // Iterating Over Bodies
        // Purpose: To iterate through every body (an object that can move and interact within the physics world) present in the simulation. Bodies represent the physical objects in the world.
        // Operation: Starts with the first body in the world's body list, and continues to the next body until there are no more bodies to process. Each body may represent a cluster or a single element in the visualization, depending on how the simulation is set up.

        for (let fixture = body.getFixtureList(); fixture; fixture = fixture.getNext()) {
            // note: fixture is defined in function createClusterBody()

            // Iterating Over Fixtures of Each Body
            // Purpose: For each body iterated over by the outer loop, this loop iterates through all fixtures attached to that body. Fixtures define the physical properties (shape, density, friction, etc.) of a part of a body. In the context of this code, each fixture represents a circle (or part of a cluster) in the visualization.
            // Operation: Starts with the first fixture of the current body, and continues to the next fixture until there are no more fixtures to process. This loop is necessary because a single body might represent a cluster of circles, with each circle represented by a fixture on the body.
            if(fixture.getShape().getType() === planck.Circle.TYPE) {
                // @ts-ignore
                let center = body.getWorldPoint(fixture.getShape().getCenter()); // Calculate the Circle's Center in World Coordinates
                let rawCircle: any = fixture.getUserData(); // Retrieve the Original Data Object. This line fetches the user data associated with the fixture. When creating fixtures, it's possible to attach arbitrary data to them using setUserData. Here, it's assumed that each fixture has a corresponding data object (rawCircle) that represents the circle's original properties (such as its position before the simulation).
                // console.log('center: ', center, [center.x, center.y])
                // console.log('rawCircle: ', rawCircle, [rawCircle.x, rawCircle.y])
                // console.log('---- after: ')

                // if (rawCircle.data.name === 'A3') {
                // console.log('\n======== A3 ==========')
                // console.log('center: ', center)
                // console.log('rawCircle: ', rawCircle)
                // console.log([center.x, center.y], [rawCircle.x, rawCircle.y])
                // console.log('rawCircle type: ', typeof rawCircle)
                // // console.log('\n')
                // }

                // Adjust cluster positions in the hierarchy: For each fixture (representing a circle in a cluster), its new position is calculated based on the simulation's outcomes, and the corresponding data object (rawCircle) is updated with these new coordinates. 调整层次结构中的簇位置：对于每个夹具（代表簇中的圆），其新位置是根据模拟结果计算的，并使用这些新坐标更新相应的数据对象 (rawCircle)。
                // The code then updates the rawCircle object with the new coordinates (center.x and center.y) obtained from the simulation. This effectively syncs the circle's visual representation with its latest position as determined by the physics engine.
                const rawCircleCopy = _.cloneDeep(rawCircle);
                // const rawCircleCopy = {...rawCircle}
                rawCircleCopy.x = center.x; // Update the Circle's Position Based on Simulation Outcomes
                rawCircleCopy.y = center.y;
                // rawCircle.x = center.x; // Update the Circle's Position Based on Simulation Outcomes
                // rawCircle.y = center.y;

                // console.log('rawCircle: ', rawCircle)
                // console.log('rawCircleCopy: ', rawCircleCopy)

                // add data update in react+ts
                // 现在这里加完所有的leaf nodes，再在for-loop外面一起更新state
                leafNodes_UpdatedXY.push(rawCircleCopy)

                // summary: layout最终的output是Updating Circle Positions: For each fixture (circle), the function calculates its new position in the world coordinates and updates the corresponding rawCircle data object with these new coordinates. This step is critical because it translates the simulated physical positions back into data that can be used for visual representation.
                // Visual Representation Synchronization: By updating the rawCircle objects with new positions, the layoutClusters() function ensures that the visualization (which relies on these data objects for drawing circles or clusters) accurately reflects the latest arrangement as determined by the physics simulation. This means that the visual layout of clusters and circles seen by the user is directly influenced by the simulated physical interactions and movements.

                // if (rawCircle.data.name === 'A3') {
                // console.log('center: ', center, [center.x, center.y])
                // console.log('rawCircle: ', rawCircle, [rawCircle.x, rawCircle.y])
                // console.log('\n')
                // }
            }
        }
    }

    // update hierarchyRoot data (with new (x, y) coordinates for each leaf nodes)
    // console.log('leafNodes_UpdatedXY: ', leafNodes_UpdatedXY)
    // console.log('hierarchyRoot: ', hierarchyRoot)
    const updateNode = (node: any, leafNodes_UpdatedXY: any): any => {
    if (node.children && node.children.length > 0) {
        // Recursively update children
        const updatedChildren = node.children.map((child: any) => updateNode(child, leafNodes_UpdatedXY));

        const updatedNonLeafNode = _.cloneDeep(node)
        updatedNonLeafNode.children = updatedChildren
        return updatedNonLeafNode
        // return { ...node, children: updatedChildren };
    } else {
        // It's a leaf node, check if there's an update in updatedLeafNodes
        const update = leafNodes_UpdatedXY.find((leafNode: any) => leafNode.data.name === node.data.name);

        // console.log('node: ', node, typeof node)
        // console.log('update: ', update, typeof update)

        if (update) {
        // Return a new node object with updated x, y values
        const updatedNode = node
        updatedNode.x = update.x
        updatedNode.y = update.y

        return updatedNode
        // return { ...node, x: update.x, y: update.y };
        }
        // No update found for this node, return it as is
        return node;
    }
    }
    const hierarchyRootCopy = _.cloneDeep(hierarchyRoot);
    const updatedHierarchyRoot = updateNode(hierarchyRootCopy, leafNodes_UpdatedXY)
    // console.log('hierarchyRoot: ', hierarchyRoot)
    // console.log('updatedHierarchyRoot: ', updatedHierarchyRoot)

    return updatedHierarchyRoot
}
export const lp = (hierarchyRoot: any, padding: number) => {
    // console.log('hierarchyRoot (rootNode): ', hierarchyRoot, typeof(hierarchyRoot))
    // console.log('\n')
    // yifang's data: `rootNode` (除了没有`uncertainty`, 其他都有)

    // 根据node depth从最底层(leaf node)开始往上遍历.
    // 虽然下面是layerDepth是从leaf node height - 1 开始，但是其实本质是从leaf node开始
    // Iterate over layers from bottom to top: Starting from the layer just above the deepest layer (leaf nodes), it iterates upwards through the hierarchy.
    let hierarchyRoot_updated = hierarchyRoot
    for(let layerDepth = hierarchyRoot.height - 1; layerDepth >= 0; layerDepth--) {
        // For each layer:

        // console.log('========= layerDepth: ', layerDepth, '=========');

        // Get clusters of circles on this layer.
        let layerClusters = getLayerClusters(hierarchyRoot_updated, layerDepth, padding);
        // let layerClusters = getLayerClusters(hierarchyRoot, layerDepth, padding);
        // console.log('layerClusters: ', layerClusters);

        // It then prepares to layout these clusters by first sorting them based on their parent's parent (this could be for ensuring a logical spatial arrangement that respects the hierarchical structure).
        // Sort clusters by parents parent, to set correct center of attraction for bodies.
        let pps: any[] = []
        layerClusters.forEach(function(cluster: any) {
            pps.push(cluster.parent.parent);
            // console.log('cluster: ', cluster);
            // console.log('cluster.nodes: ', cluster.nodes[0]['data']['name'])
            // console.log('cluster.parent: ', cluster.parent['data']['name'])
            // console.log('cluster.parent.parent: ', cluster.parent['parent'])
            // console.log('\n')
        });
        // Unique parent's parents (pps) are identified to avoid duplicate processing.
        pps = _.uniq(pps)
        // console.log('pps: ', pps);

        // Do the layout.
        // For each unique parent's parent, it filters clusters belonging to it, aggregates their nodes into a list, calculates a centroid for these nodes, and then lays out the clusters around this centroid.
        if (pps.length > 0 && pps[0] !== null) {
        pps.forEach((pp) => {
            // console.log('pp: ', pp);
            const currentPPClusters = layerClusters.filter(function(cluster: any) {
                // console.log('cluster: ', cluster, )
                // console.log('cluster.parent.parent: ', cluster.parent.parent)
                // console.log('pp: ', pp)
                // console.log('cluster.parent.parent === pp: ', cluster.parent.parent === pp)
                // Object.keys(cluster.parent.parent).forEach((key) => {
                // if (cluster.parent.parent[key] !== pp[key]) {
                //     console.log('Different attribute:', key);
                // }
                // });
                // Object.keys(cluster.parent.parent.data.children).forEach((key) => {
                // if (cluster.parent.parent.data.children[key] !== pp[key]) {
                //     console.log('Different attribute:', key);
                // }
                // });
                // console.log('cluster.parent.parent.data.name === pp.data.name: ', cluster.parent.parent.data.name === pp.data.name)
                // console.log('\n')

                return cluster.parent.parent.data.name === pp.data.name; // TODO: check为啥data和children的值会不一样
                // return cluster.parent.parent === pp;
            });
            // console.log('currentPPClusters: ', currentPPClusters)

            let circleList: any[] = [];
            currentPPClusters.forEach(function(cluster: any) {
                circleList = circleList.concat(cluster.nodes);
            });
            let centroid = getCircleCentroid(circleList);

            // console.log('pp: ', pp.data.name)
            // console.log('currentPPClusters: ', currentPPClusters.map((cluster: any) => cluster.nodes.map((node: any) => node.data.name)))
            // console.log('circleList: ', circleList.map((circle) => circle.data.name))
            // console.log('centroid: ', centroid)

            hierarchyRoot_updated = layoutClusters(hierarchyRoot_updated, currentPPClusters, centroid);
            // layoutClusters(hierarchyRoot, currentPPClusters, centroid);
            // console.log('\n')
        });
        }

        // console.log('\n\n');
    }

    // // the output is rootNode with updated x, y positions
    // console.log('updated hierarchyRoot: ', hierarchyRoot_updated)
    return hierarchyRoot_updated
}

// 2-2. calc contour
type Vec2Type = {
    x: number;
    y: number;
    distance: (vec: Vec2) => number;
    sub: (vec: Vec2) => Vec2;
    add: (vec: Vec2) => Vec2;
    scale: (scale: number) => Vec2;
    angle: (vec: Vec2) => number;
    magnitude: () => number;
    toUnitVector: () => Vec2;
}
class Vec2 implements Vec2Type {
    public x: number;
    public y: number;

    constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
    }

    public distance(vec: Vec2): number {
    const deltaX = this.x - vec.x;
    const deltaY = this.y - vec.y;
    return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    }

    public sub(vec: Vec2): Vec2 {
    return new Vec2(this.x - vec.x, this.y - vec.y);
    }

    public add(vec: Vec2): Vec2 {
    return new Vec2(this.x + vec.x, this.y + vec.y);
    }

    public scale(scale: number): Vec2 {
    return new Vec2(this.x * scale, this.y * scale);
    }

    public angle(vec: Vec2): number {
    let result = Math.atan2(vec.y, vec.x) - Math.atan2(this.y, this.x);
    if (result < 0) result += 2 * Math.PI;
    return result;
    }

    public magnitude(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y);
    }

    public toUnitVector(): Vec2 {
    return this.scale(1.0 / this.magnitude());
    }
}
type CircleType = {
    center: Vec2;
    radius: number;
    intersects: (circle: Circle) => boolean;
    intersectionPoints: (circle: Circle) => Vec2[];
}
class Circle implements CircleType {
    public center: Vec2;
    public radius: number;

    constructor(x: number, y: number, radius: number) {
    this.center = new Vec2(x, y);
    this.radius = radius;
    }

    // See: http://paulbourke.net/geometry/circlesphere/
    public intersects(circle: Circle): boolean {
    const distance = this.center.distance(circle.center);

    // Circles are to far from each other.
    if (distance > this.radius + circle.radius) return false;

    // One circle is contained in the other.
    if (distance < Math.abs(this.radius - circle.radius)) return false;

    // Circles intersect.
    return true;
    }

    public intersectionPoints(circle: Circle): Vec2[] {
    const P0 = this.center;
    const P1 = circle.center;

    const d = P0.distance(P1);
    const a = (this.radius * this.radius - circle.radius * circle.radius + d * d) / (2 * d);
    const h = Math.sqrt(this.radius * this.radius - a * a);

    const P2 = P1.sub(P0).scale(a / d).add(P0);

    const x3 = P2.x + h * (P1.y - P0.y) / d;
    const y3 = P2.y - h * (P1.x - P0.x) / d;
    const x4 = P2.x - h * (P1.y - P0.y) / d;
    const y4 = P2.y + h * (P1.x - P0.x) / d;

    return [new Vec2(x3, y3), new Vec2(x4, y4)];
    }
}
class Arc {
    center: Vec2;
    startAngle: number;
    endAngle: number;
    radius: number;

    constructor(x: number, y: number, startAngle: number, endAngle: number, radius: number) {
    this.center = new Vec2(x, y); // Assuming Vec2 is a class with a constructor
    this.startAngle = startAngle;
    this.endAngle = endAngle;
    this.radius = radius;
    }
}
// Get index and intersection point of next circle on border, in counter-clockwise direction.
// The parameter 'direction' points into the direction, where the first intersection with current circle was found.
// interface Intersection {
//     intersectionPoint: Vec2;
//     circleIndex: number;
// }
export const getNextClockwiseIntersection = (currentCircleIndex: number, circleArray: Circle[], direction: any): MainViewTypes.Intersection | undefined => {
    // console.log('--> func getNextClockwiseIntersection(): ')
    // console.log('circleArray: ', circleArray)
    // const testCircle = circleArray[0]
    // const anotherCircle = circleArray[1]
    // const testCircle = new Circle(10, 22, 15 + 2)
    // const anotherCircle = new Circle(20, 30, 10)
    // console.log('testCircle: ', testCircle)
    // const doIntersect = testCircle.intersects(anotherCircle);
    // console.log('Do circles intersect?', doIntersect);

    let currentCircle = circleArray[currentCircleIndex];
    let allIntersections: MainViewTypes.Intersection[] = [];

    for (let i = 0; i < circleArray.length; i++) {
        if (!(i === currentCircleIndex)) {
            if (circleArray[i].intersects(circleArray[currentCircleIndex])) {
                let intersectionPoints = circleArray[i].intersectionPoints(circleArray[currentCircleIndex]);
                // Store intersection points and index of corresponding circle
                allIntersections.push({
                    'intersectionPoint': intersectionPoints[0],
                    'circleIndex': i
                });
                allIntersections.push({
                    'intersectionPoint': intersectionPoints[1],
                    'circleIndex': i
                });
            }
        }
    }

    let smallestAngle = 7; // Init with max angle (> 2*PI).
    let intersectionWithSmallestAngle: MainViewTypes.Intersection | undefined = undefined; // Init as undefined.
    allIntersections.forEach(function (intersection) {
        let angle = direction.angle(intersection.intersectionPoint.sub(currentCircle.center));

        if (angle > FLOATINGPOINT_EPSILON && angle < smallestAngle) {
            smallestAngle = angle;
            intersectionWithSmallestAngle = intersection;
        }
    });

    return intersectionWithSmallestAngle;
}
// Get ring of circles that defines the outer border, together with the corresponding intersection points.
const FLOATINGPOINT_EPSILON = 0.00001;
export const getOuterCircleRing = (circles: Circle[], curvature: number) => {
    // Create deep copy of circles, as they are modified in the next steps.
    // opt 1
    let circlesEnlarged = circles.map(circle => {
    const circleCopy = _.cloneDeep(circle)
    circleCopy.radius = circleCopy.radius + curvature
    return circleCopy
    // return ({ ...circle, radius: circle.radius + curvature })
    });
    // // opt 2
    // //let circlesEnlarged = circles.map(a = > Object.assign({}, a));
    // let circlesEnlarged = circles.map(function (a) {
    //     return Object.assign({}, a)
    // });
    // // Add the radius s of the tangent circles to avoid self-intersections.
    // circlesEnlarged.forEach(function (circle: Circle) {
    //     circle.radius += curvature;
    // });
    // console.log('circlesEnlarged: ', circlesEnlarged, typeof circlesEnlarged)

    // Find index of the leftmost circle.
    let leftmostCircleIndex = 0;
    for (let i = 1; i < circlesEnlarged.length; i++) {
        if (circlesEnlarged[i].center.x - circlesEnlarged[i].radius < circlesEnlarged[leftmostCircleIndex].center.x - circlesEnlarged[leftmostCircleIndex].radius) {
            leftmostCircleIndex = i;
        }
    }

    // Get the outer ring of circles.
    let outerCircleRing: any[] = [];
    let index = leftmostCircleIndex;
    let referenceDirection = new Vec2(-1, 0);
    while (true) {
        let intersection = getNextClockwiseIntersection(index, circlesEnlarged, referenceDirection);
        if (intersection === undefined)
            break;

        index = intersection.circleIndex;
        let circle = circles[index];
        referenceDirection = intersection.intersectionPoint.sub(circle.center);

        if (outerCircleRing[0] && index === outerCircleRing[0].circleIndex && intersection.intersectionPoint.distance(outerCircleRing[0].intersectionPoint) < FLOATINGPOINT_EPSILON) {
            break;
        }

        outerCircleRing.push({
            'circle': circle,
            'intersectionPoint': intersection.intersectionPoint,
            'circleIndex': index
        });
    }

    return outerCircleRing;
}
// Generate arcs that describe the outer border of circles.
export const generateCircleArcs = (outerCircleRing: any) => {
    let arcs = [];

    for (let i = 0; i < outerCircleRing.length; i++) {
        let circle = outerCircleRing[i].circle;
        let firstIntersection = outerCircleRing[i].intersectionPoint;
        let secondIntersection = outerCircleRing[(i + 1) % outerCircleRing.length].intersectionPoint;

        let centerToFirstIntersection = firstIntersection.sub(circle.center);
        let centerToSecondIntersection = secondIntersection.sub(circle.center);
        let arcStartAngle = new Vec2(0, -1).angle(centerToFirstIntersection);
        let arcEndAngle = new Vec2(0, -1).angle(centerToSecondIntersection);

        arcs.push(new Arc(circle.center.x, circle.center.y, arcStartAngle, arcEndAngle, circle.radius));
    }

    return arcs;
}
// Generate tangent arcs that fill the space between circle arcs.
export const generateTangentArcs = (outerCircleRing: any[], curvature: number) => {
    let arcs = [];

    for (let i = 0; i < outerCircleRing.length; i++) {
        let intersection = outerCircleRing[i].intersectionPoint;
        let firstCircle = outerCircleRing[(i > 0) ? i - 1 : outerCircleRing.length - 1].circle;
        let secondCircle = outerCircleRing[i].circle;

        let intersectionToFirstCenter = firstCircle.center.sub(intersection);
        let intersectionToSecondCenter = secondCircle.center.sub(intersection);
        let arcEndAngle = new Vec2(0, -1).angle(intersectionToFirstCenter);
        let arcStartAngle = new Vec2(0, -1).angle(intersectionToSecondCenter);

        arcs.push(new Arc(intersection.x, intersection.y, arcStartAngle, arcEndAngle, curvature));
    }

    return arcs;
}
export const transformPath = (d: string, transformX: number, transformY: number): string => {
    // Splitting the input path data to extract the coordinates
    const parts = d.split("A");
    const move_to = parts[0].slice(1); // Removes the leading 'M'
    const arc_to1 = parts[1];
    const arc_to2 = parts[2].slice(0, -1); // Removes the trailing 'Z'

    // Extracting coordinates from the move_to command
    const [start_x, start_y] = move_to.split(",").map(Number);

    // Applying the transformation
    const transformed_start_x = start_x + transformX;
    const transformed_start_y = start_y + transformY;

    // Splitting the arc commands to apply transformations separately
    const arc_params1 = arc_to1.split(",");
    const [arc_end_x1, arc_end_y1] = arc_params1.slice(-2).map(Number);
    const transformed_arc_end_x1 = arc_end_x1 + transformX;
    const transformed_arc_end_y1 = arc_end_y1 + transformY;

    const arc_params2 = arc_to2.split(",");
    const [arc_end_x2, arc_end_y2] = arc_params2.slice(-2).map(Number);
    const transformed_arc_end_x2 = arc_end_x2 + transformX;
    const transformed_arc_end_y2 = arc_end_y2 + transformY;

    // Reconstructing the path with transformations applied
    return `M${transformed_start_x},${transformed_start_y}A${arc_params1.slice(0, -2).join(',')},${transformed_arc_end_x1},${transformed_arc_end_y1}A${arc_params2.slice(0, -2).join(',')},${transformed_arc_end_x2},${transformed_arc_end_y2}Z`;
}
export const parseTranslate = (transformString: string): { transformX: number; transformY: number } => {
    const regex = /translate\(([^,]+),([^)]+)\)/;
    const match = transformString.match(regex);

    if (match) {
    const transformX = parseFloat(match[1]);
    const transformY = parseFloat(match[2]);
    return { transformX, transformY };
    }

    // Return default values if parsing fails
    return { transformX: 0, transformY: 0 };
}
export const arcsToPaths = (arcs: any) => {
    let paths: any[] = [];
    let arcGen = d3.arc();

    arcs.forEach(function (arc: any) {
        let startAngleTemp = arc.startAngle;

        if (startAngleTemp > arc.endAngle) {
            startAngleTemp -= 2 * Math.PI;
        }

        paths.push({
            d: arcGen({
                innerRadius: arc.radius,
                outerRadius: arc.radius,
                startAngle: startAngleTemp,
                endAngle: arc.endAngle
            }),
            transform: "translate(" + arc.center.x + "," + arc.center.y + ")"
        });
    });

    return paths;
}
export const parsePath = (path: string): { startPoint: [number, number]; endPoint: [number, number]; numbers: number[] } => {
    // Function to parse the start and end points of a path
    const numbers = path.match(/[-\d.]+/g)?.map(Number) ?? [];
    // console.log('numbers: ', numbers)
    const startPoint: [number, number] = [numbers[0], numbers[1]];
    const endPoint: [number, number] = [numbers[numbers.length - 2], numbers[numbers.length - 1]];
    return { startPoint, endPoint, numbers };
}
export const swapStartAndEndPoint = (path: string, currStartPoint: [number, number], currEndPoint: [number, number], currPointNumbers: number[]) => {
    // console.log('--> \npath', path)
    // console.log('currPointNumbers: ', currPointNumbers)

    const swapElements = (arr: number[]) => {
    [arr[0], arr[arr.length - 2]] = [arr[arr.length - 2], arr[0]];
    [arr[1], arr[arr.length - 1]] = [arr[arr.length - 1], arr[1]];
    return arr;
    }
    const swapedPointNumbers = swapElements(currPointNumbers)
    const swapedPath = `M${swapedPointNumbers[0]},${swapedPointNumbers[1]} A ${swapedPointNumbers[2]},${swapedPointNumbers[3]},${swapedPointNumbers[4]},${swapedPointNumbers[5]},${swapedPointNumbers[6]},${swapedPointNumbers[7]},${swapedPointNumbers[8]}`

    return swapedPath;
}
export const removeSecondACommand = (d: string, pathIdx: number): string => {
    // 1. remove the second A info (回流) in each path (enable filling colors for contours)
    const parts = d.split('A')
    if (parts.length > 2) {
    const modifiedD = `${parts[0]}A${parts[1]} `
    return modifiedD;
    }
    return d; // Return original if less than 2 'A' commands
}
export const removePathReflow = (dPathList: { d: string; transform: string; label: string; }[]) => {
    const pathListUpdated = dPathList.map((path, pathIdx: number) => {
    return {
        d: removeSecondACommand(path.d, pathIdx),
        transform: path.transform,
        label: path.label
    }
    })
    return pathListUpdated
}
export const contour = (nodes: any, curvature: number) => {

    // 1. generate outer ring list
    let circles: Circle[] = [];
    nodes.forEach((node: any) => {
        circles.push(new Circle(node.x, node.y, node.r + node.contourPadding)); // Add circles with radius increased by padding. This generates the spacing between circle and contour.
    });
    let outerCircleRing = getOuterCircleRing(circles, curvature);

    // 2. generate [arc parameters], based on this outer ring
    let arcs: Arc[] = [];
    const circleArcs: Arc[] = arcs.concat(generateCircleArcs(outerCircleRing));
    const tangentArcs: Arc[] = arcs.concat(generateTangentArcs(outerCircleRing, curvature));
    arcs = circleArcs.concat(tangentArcs);

    // 3. generate paths, based on arc parameters
    const arcToPath_withTransform = (arcs: Arc[]) => {
    const paths = arcsToPaths(arcs)
    const pathsTransformed = paths.map(arc => {
        const { transformX, transformY } = parseTranslate(arc.transform)
        const newPath = transformPath(arc.d, transformX, transformY)
        return {
        d: newPath,
        transform: 'translate(0,0)',
        label: '',
        }
    })
    // console.log('pathsTransformed: ', pathsTransformed)

    return pathsTransformed
    }
    let circleArcPathList = arcToPath_withTransform(circleArcs)
    let tangentArcPathList = arcToPath_withTransform(tangentArcs)
    // console.log('circleArcPathList: ', circleArcPathList)
    // console.log('tangentArcPathList: ', tangentArcPathList)

    // post process-2: add arc label to path: 'circle-arc' vs. 'tangent-arc'
    circleArcPathList = circleArcPathList.map(path => {
    return {
        d: path.d,
        transform: 'translate(0,0)',
        label: 'circle-arc'
    }
    })
    tangentArcPathList = tangentArcPathList.map(path => {
    return {
        d: path.d,
        transform: 'translate(0,0)',
        label: 'tangent-arc'
    }
    })
    // post process-3: remove reflow
    circleArcPathList = removePathReflow(circleArcPathList)
    tangentArcPathList = removePathReflow(tangentArcPathList)
    // console.log('post process-3: ')
    // console.log('circleArcPathList: ', circleArcPathList)
    // console.log('tangentArcPathList: ', tangentArcPathList)

    // post process-4: for tangentArcPathList: (1) switch start and end points (2) transform sweep-flag from 1 to 0
    const switchAndFlipTangentArcPathList = (
    tangentArcPathList: { d: string; transform: string; label: string; }[]
    ): { d: string; transform: string; label: string; }[] => {
    // (1) tangentArcPathList: switch start and end points
    const tangentArcPathListSwitched = tangentArcPathList.map((path, idx) => {
        const parsedPathData = parsePath(path.d)
        const startPoint = parsedPathData.startPoint
        const endPoint = parsedPathData.endPoint
        const numbers = parsedPathData.numbers
        const swapedPath = swapStartAndEndPoint(path.d, startPoint, endPoint, numbers)
        return {
        d: swapedPath,
        transform: path.transform,
        label: path.label
        }
    })

    // (2) transform sweep-flag from 1 to 0
    const tangentArcPathListSwitchedFlipped = tangentArcPathListSwitched.map(path => {
        const pathDFlipped = path.d.replace(/0,0,1/g, '0,0,0');
        return {
        d: pathDFlipped,
        transform: path.transform,
        label: path.label,
        }
    })

    return tangentArcPathListSwitchedFlipped
    }
    tangentArcPathList = switchAndFlipTangentArcPathList(tangentArcPathList)
    // console.log('post process-4: ')
    // console.log('circleArcPathList: ', circleArcPathList)
    // console.log('tangentArcPathList: ', tangentArcPathList)


    // 4. combine all paths into a single path
    // note: circleArcPathList & tangentArcPathList需要穿插进行，才能完成顺时针闭合
    const concatTwoPathTypes = (
    circleArcPathList: { d: string; transform: string; label: string; }[],
    tangentArcPathList: { d: string; transform: string; label: string; }[],
    ): { d: string; transform: string; label: string; }[] => {
    const allArcPathList: { d: string; transform: string; label: string; }[] = []
    for (let i = 0; i < circleArcPathList.length; i++) {
        // console.log('i: ', i)
        // console.log('circleArcPathList[i]: ', circleArcPathList[i])

        allArcPathList.push(circleArcPathList[i])

        if (i < circleArcPathList.length - 1) {
        allArcPathList.push(tangentArcPathList[i+1])
        // console.log('tangentArcPathList[i+1]: ', tangentArcPathList[i+1])
        } else {
        allArcPathList.push(tangentArcPathList[0])
        // console.log('tangentArcPathList[0]: ', tangentArcPathList[0])
        }
        // console.log('\n')
    }

    return allArcPathList
    }
    let allArcPathList = concatTwoPathTypes(circleArcPathList, tangentArcPathList)
    // console.log('allArcPathList: ', allArcPathList)

    // post process-5:
    // 3. 非第一个点：去掉A前面("M....")的起点坐标（因为就是上一个点的终点坐标）
    allArcPathList = allArcPathList.map((arcPathData, idx) => {
    let dPath = arcPathData.d
    if (idx !== 0) {
        dPath = dPath.replace(/M.*?A/, 'A')
    } else {
        // pass
    }
    return {
        d: dPath,
        transform: arcPathData.transform,
        label: arcPathData.label,
    }
    })

    // combine path
    let combinedPathD = ''// if arc is empty, return d = ''
    if (allArcPathList.length > 0) {
    combinedPathD = allArcPathList.map(path => path.d).join(" ") + 'Z'
    }
    // console.log('combinedPathD: ', combinedPathD)

    return {
    d: combinedPathD,
    transform: 'translate(0,0)',
    }
}
export const contourHierarchy = (hierarchyRoot: any, padding: number, curvature: number) => {
    let contours: any[] = [];

    for(let layerDepth = hierarchyRoot.height - 1; layerDepth >= 0; layerDepth--) {
        // console.log('=============== layerDepth: ', layerDepth, ' ===============')

        // Get clusters of circles on this layer.
        let layerClusters = getLayerClusters(hierarchyRoot, layerDepth, padding);
        // console.log('layerClusters: ', layerClusters);

        // Create contour for each cluster.
        layerClusters.forEach((cluster: any) => {
            // console.log('cluster: ', cluster);

            // 1. recursively to find parent node (with depth === 1)name and depth
            let currentNode = cluster
            while (currentNode.parent && currentNode.parent.depth !== 1) {
                currentNode = currentNode.parent;
            }
            // console.log('currentNode: ', currentNode)
            let parentNodeData: any = {}
            if (currentNode.parent) {
                parentNodeData = {
                    name: currentNode.parent.data.name,
                    type: currentNode.parent.data.type,
                    depth: currentNode.parent.depth, // depth === 1 is the entity type level
                }
            } else {
                parentNodeData = {
                    name: 'root\'s root',
                    type: '',
                    depth: -1, // depth === 1 is the entity type level
                }
            }

            // 2. generate contour for each cluster
            // console.log('cluster.nodes: ', cluster.nodes)
            let generatedContour = contour(cluster.nodes, curvature);

            // // Assign color to contour.
            // generatedContour.forEach(function(segment: any) {
            //     segment.strokeWidth = cluster.parent.uncertainty;
            // });


            contours = contours.concat({
                d: generatedContour.d,
                transform: generatedContour.transform,
                'parent_entity_type': parentNodeData.name,
            });

            // contours = contours.concat({
            //     generatedContour: generatedContour,
            //     parentNodeData: parentNodeData
            // });
            // contours = contours.concat(generatedContour);
        });
    }

    return contours;
}